import React, { useCallback } from 'react';
import { Box, Typography, Chip, Tooltip, useTheme, Badge } from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { SPACING } from '../../../../theme';
import StarRating from '../StarRating';
import FeedbackIndicator from '../FeedbackIndicator';
import PropTypes from 'prop-types';

/**
 * CardHeader Component
 * Displays template title, version, rating, and policy shield
 */
const CardHeader = ({ template, analytics, showAnalytics }) => {
  const theme = useTheme();
  const {
    total_executions = 0,
    success_rate = 0,
    success_rate_percentage = 0,
    avg_rating = 0,
    total_feedback_count = 0,
    policy_enabled = false,
    policy_count = 0,
  } = analytics || {};

  const successRate = success_rate_percentage || success_rate * 100 || 0;
  const hasExecutions = total_executions > 0;

  // Policy check
  const hasPolicyEnabled =
    policy_enabled ||
    template.policy_enabled ||
    template.has_policy_enabled ||
    false;
  const policyCount = policy_count || template.policy_count || 0;

  // Format model configuration for tooltip
  const formatModelConfig = useCallback(() => {
    const rawConfig = template?.llm_config || template?.model_config;
    if (!rawConfig) {
      return { provider: 'N/A', model: 'N/A' };
    }

    let parsed = rawConfig;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { provider: 'N/A', model: 'N/A' };
      }
    }

    return {
      provider: parsed.provider || 'N/A',
      model: parsed.model_name || parsed.model || 'N/A',
      temperature: parsed.temperature,
      max_tokens: parsed.max_tokens,
      top_p: parsed.top_p,
      frequency_penalty: parsed.frequency_penalty,
      presence_penalty: parsed.presence_penalty,
    };
  }, [template]);

  // Create tooltip content for version chip
  const createVersionTooltip = useCallback(() => {
    const modelConfig = formatModelConfig();

    // Apple-style colors from theme
    const isDark = theme.palette.mode === 'dark';
    const bgColor = isDark
      ? theme.palette.grey[900]
      : theme.palette.background.paper;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const primaryColor = theme.palette.primary.main;
    const warningColor = theme.palette.warning.main;

    return (
      <Box
        sx={{
          p: 2.5,
          maxWidth: 380,
          bgcolor: bgColor,
          color: textPrimary,
          borderRadius: 2,
          boxShadow: theme.shadows[4],
          border: 'none',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            color: primaryColor,
            fontSize: '0.95rem',
            mb: 1.5,
            letterSpacing: '0.01em',
          }}
        >
          Model Configuration
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Provider:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.provider}
            </Box>
          </Typography>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Model:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.model}
            </Box>
          </Typography>
          {modelConfig.temperature !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Temperature:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.temperature}
              </Box>
            </Typography>
          )}
          {modelConfig.max_tokens !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Max Tokens:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.max_tokens}
              </Box>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }, [formatModelConfig, theme]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      mb={SPACING.sm}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.3,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {template.template_name || template.name || 'Unnamed Template'}
        </Typography>

        {/* Rating - Compact */}
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          <StarRating
            rating={avg_rating || 0}
            maxRating={5}
            size="small"
            showValue={avg_rating > 0}
            color={avg_rating > 0 ? 'primary' : 'disabled'}
            totalFeedbackCount={Number(total_feedback_count) || 0}
          />
          {showAnalytics && hasExecutions && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              {total_executions} exec • {successRate.toFixed(1)}% success
            </Typography>
          )}
        </Box>

        {/* Updated date */}
        {template.updated_at && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Updated: {new Date(template.updated_at).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      {/* Right side: Version badge and shield */}
      <Box display="flex" gap={1.5} alignItems="flex-start" ml={1}>
        {hasPolicyEnabled && (
          <Tooltip
            title={
              policyCount > 0
                ? `Content Check Policy Enabled (${policyCount} policy${policyCount > 1 ? 'ies' : ''})`
                : 'Content Check Policy Enabled'
            }
            arrow
          >
            <Badge
              badgeContent={policyCount > 0 ? policyCount : undefined}
              color="primary"
            >
              <SecurityIcon />
            </Badge>
          </Tooltip>
        )}
        <Tooltip
          title={createVersionTooltip()}
          arrow
          placement="right"
          enterDelay={300}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                padding: 0,
                maxWidth: 'none',
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.background.paper,
              },
            },
          }}
        >
          <Chip
            label={`v${template.version_number || template.version || '1'}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: 600,
              minWidth: '36px',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                transform: 'scale(1.05)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              },
            }}
          />
        </Tooltip>
        <FeedbackIndicator
          feedback={template.feedback}
          analytics={template.feedbackAnalytics}
          compact={true}
        />
      </Box>
    </Box>
  );
};

CardHeader.propTypes = {
  template: PropTypes.object.isRequired,
  analytics: PropTypes.object,
  showAnalytics: PropTypes.bool,
};

export default CardHeader;
